import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Upload, FileText, Trash2, Eye, PenTool, ChevronDown, ChevronUp, Settings, AlertCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { SignatureDisplay } from '../components/SignaturePad';
import SignatureFieldEditor from '../components/SignatureFieldEditor';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegistrationDocsPage = () => {
  const { token } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);
  const [expandedSigs, setExpandedSigs] = useState(null);
  const [signatures, setSignatures] = useState({});
  const [fieldEditorDoc, setFieldEditorDoc] = useState(null);
  const [fieldEditorContent, setFieldEditorContent] = useState(null);
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState(null);

  const authHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};

  const fetchDocs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/registration-documents`, {
        credentials: 'include', headers: authHeaders()
      });
      if (response.ok) setDocs(await response.json());
    } catch (error) { console.error('Failed to fetch:', error); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async () => {
    if (!fileBase64 || !title.trim()) { toast.error('Title and file are required'); return; }
    setUploading(true);
    try {
      // Chunked upload using pre-read base64 content
      const CHUNK_SIZE = 150000;
      const totalChunks = Math.ceil(fileBase64.length / CHUNK_SIZE);

      // 1. Init
      const initRes = await fetch(`${API_URL}/api/upload/init`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          filename: selectedFile?.name || 'document',
          file_type: selectedFile?.type || 'application/octet-stream',
          total_chunks: totalChunks,
          upload_type: 'registration_document',
          metadata: { title: title.trim(), description: description.trim() }
        })
      });
      if (!initRes.ok) { toast.error('Upload init failed'); setUploading(false); return; }
      const { upload_id } = await initRes.json();

      // 2. Send chunks
      for (let i = 0; i < totalChunks; i++) {
        const chunk = fileBase64.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const chunkRes = await fetch(`${API_URL}/api/upload/${upload_id}/chunk`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ chunk_index: i, data: chunk })
        });
        if (!chunkRes.ok) { toast.error(`Chunk ${i + 1}/${totalChunks} failed`); setUploading(false); return; }
      }

      // 3. Complete
      const completeRes = await fetch(`${API_URL}/api/upload/${upload_id}/complete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      if (completeRes.ok) {
        toast.success('Registration document uploaded!');
        setUploadOpen(false); setTitle(''); setDescription(''); setSelectedFile(null); setFileBase64(null);
        fetchDocs();
      } else { const err = await completeRes.json(); toast.error(err.detail || 'Upload failed'); }
    } catch (err) { toast.error('Upload failed: ' + (err.message || 'Unknown error')); }
    finally { setUploading(false); }
  };

  const handleDelete = async (regDocId) => {
    setDeleteConfirmDoc(regDocId);
  };

  const confirmDelete = async () => {
    const regDocId = deleteConfirmDoc;
    if (!regDocId) return;
    setDeleteConfirmDoc(null);
    try {
      const response = await fetch(`${API_URL}/api/registration-documents/${regDocId}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders()
      });
      if (response.ok) { toast.success('Document removed'); fetchDocs(); }
      else { toast.error('Failed to remove'); }
    } catch { toast.error('Failed to remove'); }
  };

  const fetchSignatures = async (regDocId) => {
    try {
      const response = await fetch(`${API_URL}/api/registration-documents/${regDocId}/signatures`, {
        credentials: 'include', headers: authHeaders()
      });
      if (response.ok) {
        const sigs = await response.json();
        setSignatures(prev => ({ ...prev, [regDocId]: sigs }));
      }
    } catch { /* ignore */ }
  };

  const toggleSigs = (regDocId) => {
    if (expandedSigs === regDocId) { setExpandedSigs(null); return; }
    setExpandedSigs(regDocId);
    if (!signatures[regDocId]) fetchSignatures(regDocId);
  };

  const openFieldEditor = async (doc) => {
    setFieldEditorDoc(doc);
    try {
      const res = await fetch(`${API_URL}/api/registration-documents/${doc.reg_doc_id}/content`, {
        credentials: 'include', headers: authHeaders()
      });
      if (res.ok) {
        setFieldEditorContent(await res.json());
      }
    } catch { /* ignore */ }
  };

  return (
    <DashboardLayout title="Registration Documents">
      <div className="max-w-3xl space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-white/60 text-sm">Documents shown to new users during registration that require signing.</p>
          <Button onClick={() => setUploadOpen(true)} className="bg-primary text-black hover:bg-primary/90 flex-shrink-0" data-testid="upload-reg-doc-btn">
            <Upload className="w-4 h-4 mr-2" /> Upload Document
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2].map(i => <div key={i} className="animate-pulse bg-black/40 border border-white/10 rounded-sm h-24" />)}
          </div>
        ) : docs.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No Registration Documents</h3>
            <p className="text-white/60 mb-6">Upload documents that users must sign during registration</p>
          </div>
        ) : (
          <div className="space-y-4">
            {docs.map(doc => (
              <div key={doc.reg_doc_id} className="bg-black/40 border border-white/10 rounded-sm p-4 sm:p-5" data-testid={`reg-doc-${doc.reg_doc_id}`}>
                <div className="flex items-start gap-3">
                  <FileText className="w-8 h-8 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium">{doc.title}</h3>
                    {doc.description && <p className="text-white/50 text-sm mt-1">{doc.description}</p>}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-white/40">
                      <span className="truncate">{doc.filename}</span>
                      <span>Uploaded by {doc.uploaded_by_name}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 flex-wrap">
                  <Button variant="ghost" size="sm" onClick={() => openFieldEditor(doc)} className="text-white/60 hover:text-white h-8 text-xs" data-testid={`manage-fields-${doc.reg_doc_id}`}>
                    <Settings className="w-4 h-4 mr-1" />
                    Fields
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleSigs(doc.reg_doc_id)} className="text-white/60 hover:text-white h-8 text-xs" data-testid={`view-sigs-${doc.reg_doc_id}`}>
                    <PenTool className="w-4 h-4 mr-1" />
                    Signatures
                    {expandedSigs === doc.reg_doc_id ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.reg_doc_id)} className="text-red-400/60 hover:text-red-400 h-8 text-xs ml-auto" data-testid={`delete-reg-doc-${doc.reg_doc_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {expandedSigs === doc.reg_doc_id && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    {signatures[doc.reg_doc_id]?.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-white/60 text-xs mb-2">{signatures[doc.reg_doc_id].length} signature(s)</p>
                        {signatures[doc.reg_doc_id].map((sig, i) => (
                          <div key={sig.signature_id || i} className="flex items-center gap-3 p-2 bg-white/5 rounded-sm">
                            <img src={sig.signature_data} alt="Signature" className="h-8 object-contain bg-white rounded-sm px-2" />
                            <div>
                              <p className="text-white text-xs">{sig.signer_name} ({sig.signer_email})</p>
                              <p className="text-white/40 text-xs">{new Date(sig.signed_at).toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-white/40 text-sm">No signatures yet</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={uploadOpen} onOpenChange={(v) => { setUploadOpen(v); if (!v) { setTitle(''); setDescription(''); setSelectedFile(null); setFileBase64(null); } }}>
          <DialogContent className="bg-[#0A0A0A] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Upload Registration Document</DialogTitle>
              <DialogDescription className="text-white/60">
                This document will be shown to users during registration and must be signed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-white/80">Document Title *</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Terms of Service, NDA..." className="mt-1 bg-black/50 border-white/10 text-white" data-testid="reg-doc-title-input" />
              </div>
              <div>
                <Label className="text-white/80">Description</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description..." className="mt-1 bg-black/50 border-white/10 text-white" data-testid="reg-doc-desc-input" />
              </div>
              <div>
                <Label className="text-white/80">File *</Label>
                <div className={`border-2 border-dashed rounded-sm p-6 text-center transition-colors mt-1 ${selectedFile ? 'border-primary/50 bg-primary/5' : 'border-white/20 hover:border-primary/50'}`}>
                  <input type="file" onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setSelectedFile({ name: f.name, type: f.type, size: f.size });
                    setFileBase64(null);
                    // Use FileReader for mobile support, process in requestAnimationFrame to stay responsive
                    const reader = new FileReader();
                    const timeout = setTimeout(() => { reader.abort(); toast.error('File read timed out'); }, 60000);
                    reader.onload = () => {
                      clearTimeout(timeout);
                      try {
                        const bytes = new Uint8Array(reader.result);
                        // Process base64 conversion in batches via setTimeout to avoid freezing UI
                        const chunks = [];
                        let offset = 0;
                        const batchSize = 32768;
                        const processBatch = () => {
                          const end = Math.min(offset + batchSize, bytes.length);
                          chunks.push(String.fromCharCode.apply(null, bytes.subarray(offset, end)));
                          offset = end;
                          if (offset < bytes.length) {
                            setTimeout(processBatch, 0);
                          } else {
                            setFileBase64(btoa(chunks.join('')));
                          }
                        };
                        processBatch();
                      } catch (err) {
                        setFileBase64(null);
                        toast.error('Failed to process file');
                      }
                    };
                    reader.onerror = () => { clearTimeout(timeout); setFileBase64(null); toast.error('Failed to read file'); };
                    reader.readAsArrayBuffer(f);
                  }} className="hidden" id="reg-doc-upload" data-testid="reg-doc-file-input" />
                  <label htmlFor="reg-doc-upload" className="cursor-pointer">
                    {selectedFile ? (
                      <>
                        <FileText className="w-8 h-8 text-primary mx-auto mb-2" />
                        <p className="text-white text-sm truncate">{selectedFile.name}</p>
                        <p className="text-white/40 text-xs mt-1">{fileBase64 ? `${(selectedFile.size / 1024).toFixed(0)}KB ready` : 'Reading...'}</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
                        <p className="text-white text-sm">Click to select file</p>
                        <p className="text-white/40 text-xs mt-1">PDF, Word, Image</p>
                      </>
                    )}
                  </label>
                </div>
              </div>
              <Button onClick={handleUpload} disabled={uploading || !title.trim() || !fileBase64}
                className="w-full bg-primary text-black hover:bg-primary/90 disabled:opacity-40" data-testid="reg-doc-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Signature Field Editor for Reg Docs */}
        <SignatureFieldEditor
          open={!!fieldEditorDoc}
          onOpenChange={(v) => { if (!v) { setFieldEditorDoc(null); setFieldEditorContent(null); } }}
          documentId={null}
          documentContent={fieldEditorContent?.content || null}
          documentType={fieldEditorContent?.file_type || fieldEditorDoc?.file_type || ''}
          documentName={fieldEditorDoc?.title || ''}
          token={token}
          isRegDoc={true}
          regDocId={fieldEditorDoc?.reg_doc_id || null}
        />

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirmDoc} onOpenChange={(v) => { if (!v) setDeleteConfirmDoc(null); }}>
          <AlertDialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Remove Registration Document
              </AlertDialogTitle>
              <AlertDialogDescription className="text-white/60">
                This will remove the document from the registration flow. Users will no longer need to sign it. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/10 text-white hover:bg-white/5" data-testid="delete-reg-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="delete-reg-confirm-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default RegistrationDocsPage;
